import React from 'react';
import Prism from 'prismjs';

import { Transforms, Element, Editor, Path, Text } from 'slate';
import { ReactEditor } from 'slate-react';
import { icons } from '@ublend-npm/aulaui-next';
import { AulaPlugin } from '../plugins';
import { codeType } from './codeType';
import { CodeIcon, CodeLine, LineNumber, LineContent } from './code.styled';
import { NestedMenuItem } from '../../components/NestedMenu/NestedMenu';
import { Code } from '../../components/Code/Code';
import { AulaEditorInstance } from '../../types';
import { isCurrentSelectionCollapsed } from '../../lib/queries';
import { insertBlock } from '../../lib/commands';
import { getDecoratedRanges } from './getDecoratedRanges';

// Prevent prism from automatically running and breaking slate
Prism.manual = true;

export const insertCodeBlock = (editor: AulaEditorInstance, path: Path) => {
  insertBlock(editor, path, [
    {
      type: codeType,
      language: undefined,
      children: [
        {
          children: [
            {
              text: '',
            },
          ],
        },
      ],
    },
  ]);
};

export const codeBlock: AulaPlugin = (editableProps, editor) => ({
  ...editableProps,
  onKeyDown: (e) => {
    if (editableProps.onKeyDown) editableProps.onKeyDown(e);
    const { selection } = editor;

    if (e.key === 'Backspace' && selection != null) {
      const [node, path] = Editor.node(editor, selection, { depth: 1 });

      if (node == null) return;

      // If the cursor is at the end of a code block and there are at least 2 empty lines
      // in the code block then pressing backspace removes them and moves cursor to a new
      // paragraph.
      if (
        Element.isElement(node) &&
        node.type === codeType &&
        Element.isElementList(node.children) &&
        isCurrentSelectionCollapsed(editor) &&
        Editor.isEnd(editor, selection.focus, path)
      ) {
        if (
          node.children.length > 1 &&
          node.children
            .slice(-2)
            .every((element) => Editor.isEmpty(editor, element))
        ) {
          e.preventDefault();

          if (node.children.length === 2) {
            // Code block only contains empty lines, remove it.
            Transforms.removeNodes(editor, { mode: 'highest' });
          } else {
            // Remove the last 2 empty lines
            Transforms.removeNodes(editor, { mode: 'lowest' });
            Transforms.removeNodes(editor, { mode: 'lowest' });
          }

          Transforms.insertNodes(editor, [{ children: [{ text: '' }] }], {
            mode: 'highest',
          });

          return;
        }

        const previous = Editor.previous(editor, {
          mode: 'highest',
          at: path,
          voids: true,
        });

        // If empty code block is the first element in the editor or below a void block pressing,
        // backspace replaces it with a paragraph
        if (
          node.children.length === 1 &&
          Editor.isEmpty(editor, node.children[0]) &&
          (path[0] === 0 || (previous && Editor.isVoid(editor, previous[0])))
        ) {
          // Changing the type of the node would change the rendering pathway and
          // cause React hooks issues so we remove and insert a new element instead
          Transforms.removeNodes(editor, { mode: 'highest' });
          Transforms.insertNodes(editor, [{ children: [{ text: '' }] }], {
            mode: 'highest',
          });
        }
      }
    }
  },
  blockMenu: {
    ...editableProps.blockMenu,
    Formatting: {
      ...(editableProps.blockMenu || {}).Formatting,
      'Code block': ({ text, element, attributes, onClose }) => (
        <NestedMenuItem
          leftIcon={
            <CodeIcon>
              <icons.Code />
            </CodeIcon>
          }
          attributes={{
            ...attributes,
            onClick: () => {
              if (onClose) {
                onClose();
              }
              insertCodeBlock(editor, ReactEditor.findPath(editor, element));
            },
          }}
          key={text}
          text={text}
        />
      ),
    },
  },
  renderSubMenuItem: (props) => {
    const { text } = props;
    return text === 'Formatting' ? (
      <NestedMenuItem
        leftIcon={<icons.FormatShapesIcon />}
        rightIcon={<icons.ArrowRight />}
        {...props}
      />
    ) : (
      editableProps?.renderSubMenuItem?.(props) || <NestedMenuItem {...props} />
    );
  },
  decorate: ([node, path]) => {
    const otherDecorations = editableProps.decorate
      ? editableProps.decorate([node, path])
      : [];

    // Text nodes within a code block will always be at level 3 of the tree as they have the
    // code line and code block type elements above them. This conditional, protects the
    // the Path.parent(Path.parent(path)) below.
    if (path.length > 2) {
      const [block] = Editor.node(editor, Path.parent(Path.parent(path)));

      // Only apply code formatting to text nodes within code block
      if (Text.isText(node) && Element.isElement(block) && block.language) {
        const tokens = Prism.tokenize(
          node.text,
          Prism.languages[block.language] || '',
        );

        // Prism returns an array of tokens, translate those to ranges for Slate to decorate.
        const ranges = getDecoratedRanges(path, tokens);

        return [...ranges, ...otherDecorations];
      }
    }

    return otherDecorations;
  },
  renderLeaf: (props) => {
    const { children, leaf, attributes } = props;

    if (!leaf.codeToken && editableProps.renderLeaf) {
      return editableProps.renderLeaf(props);
    }

    return (
      <span {...attributes} className={`prism-token token ${leaf.codeToken}`}>
        {children}
      </span>
    );
  },

  renderElement: (props) => {
    const { element, attributes, children } = props;
    const [parent] = Editor.parent(
      editor,
      ReactEditor.findPath(editor, element),
    );

    if (element.type === codeType) {
      return (
        <div {...attributes}>
          <Code element={element}>{children}</Code>
        </div>
      );
    }

    if (Element.isElement(parent) && parent.type === codeType) {
      return (
        <CodeLine {...attributes}>
          <LineNumber contentEditable={false} />
          <LineContent>{children}</LineContent>
        </CodeLine>
      );
    }

    return editableProps.renderElement?.(props) || <p />;
  },
});
